import { useMutation } from "@apollo/client";
import CancelOutlined from "@mui/icons-material/CancelOutlined";
import { Alert, Chip, ListItemIcon, ListItemText, MenuItem, Typography } from "@mui/material";
import { useAlerts } from "components/Alerts/AlertProvider";
import { CustomGridColDef, DataGrid } from "components/DataGrid";
import { Dialog } from "components/Dialog";
import { hasValue } from "encamp-shared/src/utils/hasValue";
import { gql } from "generated-graphql";
import pluralize from "pluralize";
import { useCallback, useMemo } from "react";
import { orgStatusIsOpen, orgStatusToLabel } from "util/constants";

import { useBulkActionContext } from ".";
import { Row } from "../ChemicalFacilitiesDataGrid";

const BULK_CLOSE_REPORTS = gql(`
  mutation BulkCloseTierIIReports($reportIds: [ID!]!) {
    bulkCloseTierIIReports(reportIds: $reportIds) {
      closedCount
      skippedCount
    }
  }
`);

type PreviewRow = Row & { willClose: boolean };

export function CloseReportsMenuItem() {
  const { setDialogState } = useBulkActionContext();

  return (
    <MenuItem onClick={() => setDialogState("closeReports")}>
      <ListItemIcon>
        <CancelOutlined />
      </ListItemIcon>
      <ListItemText>Close Reports</ListItemText>
    </MenuItem>
  );
}

export function CloseReportsDialog() {
  const alerts = useAlerts();
  const { dialogState, onClose, selectedRows } = useBulkActionContext();

  // Only selected rows that actually have a report can be closed; rows showing
  // "No Report" carry a null tierIIReportId and are dropped. Of the remaining
  // reports, only unverified ones (open org status) will be closed — the rest are
  // shown as excluded. Closable rows sort to the top.
  const previewRows: PreviewRow[] = useMemo(
    () =>
      selectedRows
        .filter((row) => hasValue(row.tierIIReportId))
        .map((row) => ({
          ...row,
          willClose: !!row.organizationStatus && orgStatusIsOpen(row.organizationStatus),
        }))
        // Closable rows first, then excluded.
        .sort((a, b) => {
          if (a.willClose === b.willClose) {
            return 0;
          }
          return a.willClose ? -1 : 1;
        }),
    [selectedRows]
  );

  const closableReportIds = useMemo(
    () =>
      previewRows
        .filter((row) => row.willClose)
        .map((row) => row.tierIIReportId)
        .filter(hasValue),
    [previewRows]
  );
  const closableCount = closableReportIds.length;
  const excludedCount = previewRows.length - closableCount;

  const [bulkCloseReports, { loading }] = useMutation(BULK_CLOSE_REPORTS, {
    refetchQueries: ["ChemicalFacilities"],
  });

  const handleClose = useCallback(async () => {
    try {
      const result = await bulkCloseReports({ variables: { reportIds: closableReportIds } });
      const closedCount = result.data?.bulkCloseTierIIReports.closedCount ?? 0;
      const skippedCount = result.data?.bulkCloseTierIIReports.skippedCount ?? 0;
      const skippedNote = skippedCount
        ? ` ${skippedCount} ${pluralize("report", skippedCount)} could not be closed.`
        : "";
      alerts.success(`Closed ${closedCount} ${pluralize("report", closedCount)}.${skippedNote}`);
      onClose();
    } catch (err) {
      alerts.error("An error occurred while closing reports.", err);
    }
  }, [bulkCloseReports, closableReportIds, alerts, onClose]);

  const columns: CustomGridColDef<PreviewRow>[] = useMemo(
    () => [
      { field: "organization", headerName: "Organization", flex: 1, minWidth: 130 },
      { field: "facilityName", headerName: "Facility", flex: 1, minWidth: 140 },
      { field: "state", headerName: "State", width: 70 },
      { field: "reportKind", headerName: "Type", width: 110 },
      {
        field: "organizationStatus",
        headerName: "Status",
        flex: 0.8,
        minWidth: 120,
        valueGetter: (_, row) =>
          row.organizationStatus ? orgStatusToLabel(row.organizationStatus) : "—",
      },
      {
        field: "willClose",
        headerName: "Outcome",
        width: 120,
        sortable: false,
        renderCell: (params) =>
          params.row.willClose ? (
            <Chip size="small" color="success" variant="outlined" label="Will close" />
          ) : (
            <Chip size="small" color="default" variant="outlined" label="Excluded" />
          ),
      },
    ],
    []
  );

  if (dialogState !== "closeReports") {
    return null;
  }

  return (
    <Dialog
      open
      title="Close reports?"
      onClose={onClose}
      maxWidth="md"
      fullWidth
      primaryActionButtonProps={{
        saveText: "Close reports",
        onClick: handleClose,
        loading,
        startIcon: <CancelOutlined />,
        disabled: closableCount === 0,
      }}
    >
      {previewRows.length === 0 ? (
        <Typography>
          None of the selected rows have a report to close. Only unverified reports (not yet
          verified or filed) can be closed.
        </Typography>
      ) : (
        <>
          <Typography>
            You are about to close {closableCount} {pluralize("report", closableCount)}. This
            permanently deletes {closableCount === 1 ? "the report" : "these reports"} and cannot be
            undone. Facility and chemical/inventory data are not affected, but reviewer/assignee and
            step information will be deleted.
          </Typography>
          {excludedCount > 0 && (
            <Alert severity="info">
              {excludedCount} selected {pluralize("report", excludedCount)} cannot be closed
              (already verified or filed) and will be excluded.
            </Alert>
          )}
          <DataGrid
            columns={columns}
            rows={previewRows}
            autoHeight
            hideFooter
            disableRowSelectionOnClick
            sx={{ maxHeight: 320 }}
          />
        </>
      )}
    </Dialog>
  );
}
